import axios from "axios";
import { nullText, NULL_DISPLAY_TEXT } from '../components/atoms/NullDisplay/NullDisplay';

// Check if the stored JWT token is expired.
const isTokenExpired = () => {
  const exp = window.localStorage.getItem('exp');
  if (!exp) return true;
  return new Date(exp * 1000) < new Date();
};

// Build headers with Authorization token from localStorage.
// Reads directly from localStorage rather than relying on axios.defaults,
// which may not be set yet due to React useEffect ordering (Issue #395).
// Skips expired tokens to avoid 401 errors from PostgREST (Issue #415).
const authHeaders = () => {
  const token = window.localStorage.getItem('token');
  return {
    'ngrok-skip-browser-warning': true,
    ...(token && !isTokenExpired() && { 'Authorization': `Bearer ${token}` })
  };
};

export const fetchData = async ({ endpoint, controller }) => axios.get(endpoint, {
  headers: authHeaders(),
  signal: controller.signal
});

export const simpleFetch = async (endpoint) => axios.get(endpoint,
  {
    headers: authHeaders()
  })
  .then(response => response.data).catch(error => {
    const message = error.response?.data?.details || error.response?.data?.message || error.message || 'An unexpected error occurred';
    console.error(`API error (${endpoint}):`, message);
  });

export const fetchById = async (endPoint, id) => axios.get(`${endPoint}${id}`,
  {
    headers: authHeaders()
  }
)
  .then(response => response.data).catch(error => {
    const message = error.response?.data?.details || error.response?.data?.message || error.message || 'An unexpected error occurred';
    console.error(`API error (${endPoint}${id}):`, message);
  });

export const clearHooks = (...hooks) => {
  hooks.forEach(hook => hook(null));
}

export const getDistrictInfo = (...args) => {
  fetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_resume?_irn=${args[0]}&_year=${args[1]}`, { headers: { 'ngrok-skip-browser-warning': true, 'Authorization': 'Bearer ' + args[4] } })
    .then(response => response.json())
    .then(data => {
      if (args[0] && args[1] && args[2] && args[3]) {
        args[2] && args[2](data[0]);
        args[3] && args[3](data[0]["group_name"]);
      }
    }).catch(error => alert(error));
};


export const getGroupData = (endpoint, groupId, hook, token) => {
  fetchById(endpoint, groupId, token)
    .then(data => {
      if (groupId === "Enrollment+Brackets") {
        hook(data.map((elem, i) => {
          let splitLabel = elem.label.split(')'),
            newLabel = i === 0 ? elem.label = 'Districts' + splitLabel[1].replace('U', 'u') : elem.label = 'Districts with' + splitLabel[1];
          return {
            label: newLabel,
            value: elem.value
          }
        })
        );
      } else {
        return hook(data);
      }
    });
}

export const currencyFormat = (number) => {
  return new Intl.NumberFormat("en-US", {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(number)
}

export const buildChartTooltip = ({ infoIcon, styles }) => {
  const description = infoIcon.parentElement.querySelector(".tooltip-desc");
  const chartContainer  = infoIcon.closest('.highcharts-container');
  const infoParent = infoIcon.parentElement;

  if(description && chartContainer) {
    infoIcon.onmouseover = () => {
      const descriptionHeight = description.offsetHeight;

      const containerLeft = chartContainer.getBoundingClientRect().left
      const containerRight = chartContainer.getBoundingClientRect().right
      const containerBottom = chartContainer.getBoundingClientRect().bottom

      const infoLeft = infoIcon.getBoundingClientRect().left;
      const infoRight = infoIcon.getBoundingClientRect().right;
      const infoBottom = infoIcon.getBoundingClientRect().bottom;

      description.style.visibility = "visible";
      infoParent.style.zIndex = 1

      // Calculate the position side to display the tooltip correctly
      if((infoLeft - containerLeft) > (containerRight - infoRight)) {
        description.style.right = "-10px"
      } else {
        description.style.left = "-10px"
      }
      if(styles) {
        description.style.cssText = `visibility: visible; ${styles}`
      }
      // If Tooltip height > the bottom space, it will be displayed on top.
      // The rotation changes button orientation so is not applicable.
      if(((containerBottom - infoBottom) < descriptionHeight) && !styles?.includes('rotate')) {
        description.style.bottom = "20px"
      }
    }

    infoIcon.onmouseleave = () => {
      description.style.cssText = `visibility: hidden`
      infoParent.style.zIndex = 0
    }
  }
}

export const getToken = () => {
  return window.localStorage.getItem('token');
}

export const setToken = (token, exp) => {
  window.localStorage.setItem('exp', exp);
  window.localStorage.setItem('token', token);
}

export const localHour = (datetime) => {
  const now = new Date(datetime);
  const offsetMs = now.getTimezoneOffset() * 60 * 1000;
  const dateLocal = new Date(now.getTime() - offsetMs);
  return dateLocal.toISOString().slice(0, 16).replace("T", " ");
}

/**
 * Returns a date based on time zone and US format.
 *
 * @param datetime {string}: The date provided by the component.
 * @returns {string}: The date with US format.
 */
export const localHourFormatted = (datetime) => {
  let dateConfig = { year: 'numeric', month: 'short', day: 'numeric' };
  const date = new Date(datetime);
  // one day subtracted from the original value to match with the time zone.
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  const dateLocal = new Date(date.getTime() - offsetMs);

  return dateLocal.toLocaleDateString("en-US", dateConfig);
}

export const normalizeDistributionData = ({ varName, varIrn, irns, hook, year }, token) => {
  let distributionData = {};

  let splitLD = varName.split('__');
  let variable = varName;

  if (splitLD.length > 1) {
    variable = splitLD[0];
    let planNum = splitLD[1];
    let planType = splitLD[2];

    simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_footer_health?_variable=${variable}&_irn=${varIrn}&_irns=${irns}&_year=${year}&_plannum=${planNum}&_plantype=${planType}`, token)
      .then(data => {
        data.forEach(item => {
          distributionData[item.quantity] = item.value && item.value !== 0 ? (item.value).toFixed(2) : item.value
        })
      }).then(() => {
        hook(distributionData)
      });
  } else {
    simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_footer?_variable=${varName}&_irn=${varIrn}&_irns=${irns}&_year=${year}`, token)
      .then(data => {
        /* TODO verify how to get the var_type */
        const yearVars = ['next_contract_renewal_year', 'current_contract_year'];
        data.forEach(item => {
          let itemValue;
          if(item.value && item.value) {
            itemValue = yearVars.includes(varName) ? Math.trunc(item.value) : (item.value).toFixed(0)
          } else {
            itemValue = item.value
          }
          return distributionData[item.quantity] = itemValue;
        })
      }).then(() => {
        hook(distributionData)
      });
  }
}

export const mergeArrayObjects = ({ arrX, arrY, id }) => {
  let mergedArray = []

  arrX.forEach(itemX => {
    arrY.some((itemY) => (itemX[id] === itemY[id] ? mergedArray.push({ ...itemX, ...itemY }) : null))
  })
  return mergedArray
}

export const requestAxis = ( enabledHealthBenefits, axis, axisValue, districtInfo, irns, year, hook) => {
  let axisRequest = axisValue.split('__').length <= 1 ? `${axis}:${axisValue}` : `${axis}:${axisValue.split('__')[0]}`;
  /** 
   *   The health_insurance variables has a __ delimitier.
   *   This variables are build with the following pattern:
   *       "variable"__"plannum"__"q13_plantype_p7q3"
   *   When "q13_plantype_p7q3" represents the planType,
   */

  let laborConditions = axisValue.split('__').length > 1 ? `&plannum=eq.${axisValue.split('__')[1]}&q13_plantype_p7q3=eq.${axisValue.split('__')[2]}` : '';

  if (axisValue.split('__').length <= 1) {
    if (year.value === 'MCD') {
      simpleFetch(
        `${process.env.REACT_APP_API_URL}/mcd_tabloide?select=irn,${axisRequest},year_${axisRequest}_yr,district_name&irn=in.(${irns})`
      )
        .then((data) =>
          data.forEach((item) => {
            item['year_' + axis] = item['year_' + axis] === yearList[1].value ? '' : ` (${item['year_' + axis]})`;
            if (item.irn === districtInfo.value) {
              item.color = "#F5B335";
              hook(data);
            }
          })
        )
        .catch(() => console.log("no-data"));
    } else {
      simpleFetch(
        `${process.env.REACT_APP_API_URL}/hvd_tabloide?select=irn,${axisRequest},district_name&irn=in.(${irns})&file_year=eq.${year.value}`
      )
        .then((data) =>
          data.forEach((item) => {
            if (item.irn === districtInfo.value) {
              item.color = "#F5B335";
              hook(data);
            }
          })
        )
        .catch(() => console.log("no-data"));
    }
  } else if (enabledHealthBenefits) {
    if (year.value === 'MCD') {
      simpleFetch(
        `${process.env.REACT_APP_API_URL}/mcd_hb_tabloide?select=irn,${axisRequest},district_name&irn=in.(${irns})${laborConditions}`
      )
        .then((data) =>
          data.forEach((item) => {
            if (item.irn === districtInfo.value) {
              item.color = "#F5B335";
              hook(data);
            }
          })
        )
        .catch(() => console.log("no-data"));
    } else {
      simpleFetch(
        `${process.env.REACT_APP_API_URL}/health_insurance_readable?select=irn,${axisRequest},district_name&irn=in.(${irns})&file_year=eq.${year.value}${laborConditions}`
      )
        .then((data) =>
          data.forEach((item) => {
            if (item.irn === districtInfo.value) {
              item.color = "#F5B335";
              hook(data);
            }
          })
        )
        .catch(() => console.log("no-data"));
    }
  }
}

export const groupTypes = [
  { label: 'County', value: 'County' },
  { label: 'Similarity', value: 'Similarity' },
  { label: 'District Picker', value: 'District+Picker' },
  { label: 'Enrollment Bracket', value: 'Enrollment+Brackets' },
  { label: 'Custom Groups', value: 'custom_groups' },
  { label: 'All Ohio School Districts', value: 'All' },
];

// Converts a string to TitleCase
export const toTitleCase = (str) => str.replace(/\w\S*/g, txt =>
  txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
);


// Formatted item value based on var type.
const defaultFormatter = new Intl.NumberFormat('en-US');
export const valueFormatted = (item, varType) => {
  if (item === null || item === undefined) return NULL_DISPLAY_TEXT
  const itemValue = +item

  // If itemValue is not a number return item
  if(isNaN(itemValue)) {
    return item
  }
  // Sentinel value for "not in SERB" districts.
  if (itemValue === -9876.9876 || itemValue === 9876.9876) {
    return nullText('NISS')
  }
  
  switch (varType) {
    case "$": return currencyFormat(itemValue);
    case "%": return `${itemValue.toFixed(2)}%`;
    case "year": return `${itemValue}`;
    default: return defaultFormatter.format(itemValue);
  }
}

// Money Formatted
export const moneyFormat = ({ val, base, tag }) => {
  let num = val.toString();
  if (val <= ((base * 10) - 1)) return `$${num.slice(0, 1)}.${num.slice(1, 3)}${tag}`;
  if (val <= ((base * 100) - 1)) return `$${num.slice(0, 2)}.${num.slice(2, 3)}${tag}`;
  if (val <= ((base * 1000) - 1)) return `$${num.slice(0, 3)}.${num.slice(3, 4)}${tag}`;
}

// Years data.
export const yearList = [
  { "value": 'MCD', "label": 'Most Current Data' },
  { "value": 2026, "label": "2026" },
  { "value": 2025, "label": "2025" },
  { "value": 2024, "label": "2024" },
  { "value": 2023, "label": "2023" },
  { "value": 2022, "label": '2022' },
  { "value": 2021, "label": '2021' },
  { "value": 2020, "label": '2020' },
  { "value": 2019, "label": '2019' },
  { "value": 2018, "label": '2018' },
  { "value": 2017, "label": '2017' },
  { "value": 2016, "label": '2016' },
  { "value": 2015, "label": '2015' },
  { "value": 2014, "label": '2014' },
  { "value": 2013, "label": '2013' },
  { "value": 2012, "label": '2012' },
  { "value": 2011, "label": '2011' },
  { "value": 2010, "label": '2010' },
  { "value": 2009, "label": '2009' },
  { "value": 2008, "label": '2008' },
  { "value": 2007, "label": '2007' },
  { "value": 2006, "label": '2006' }
];

export const buildOnLoad = ({ user, callback, token }) => {
  let conditionalDistrict;
  let userEmail = encodeURIComponent(user.email);
  let fixedSelection = JSON.parse(localStorage.fixedSelection ?? null);
  // Get user default info.
  simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_allowed_districts?_email=${userEmail}`)
      .then(data => {
        if (user[`${process.env.REACT_APP_AUTH0_KEY}/district`].sessionType === 'district-access') {
          localStorage.setItem('allowed_districts', JSON.stringify(data[0]));
          conditionalDistrict = [data[0]];
        } else {
          localStorage.setItem('allowed_districts', JSON.stringify(data[0].sort(function (a, b) {
            return a.label.localeCompare(b.label);
          })));
          conditionalDistrict = data[0];
        }
      }).then(() => {
    // All Ohio as default district.
    let urlGroupSelected = `${process.env.REACT_APP_API_URL}/hvd_group_selected?select=count,label,value,irns&label=eq.`;
    let groupId = "All Ohio's School Districts";
    fetchById(urlGroupSelected, groupId, token)
      .then(data => {
        callback({
          referenceDistrict: fixedSelection?.referenceDistrict ?? conditionalDistrict[0],
          irns: fixedSelection?.irns ?? data[0].irns,
          groupType: fixedSelection?.groupType ?? { label: 'All Ohio School Districts', value: 'All' },
          groupTypeOptions: null,
          groupCount: fixedSelection?.groupCount ?? data[0].count,
          groupInfo: fixedSelection?.groupInfo ?? { label: 'All Ohio School Districts', value: 1 },
          year: fixedSelection?.year ?? { "value": 'MCD', "label": 'Most Current Data' },
        })
      });
  })
}

export const allowedDomain = (rowData) => {
  // Regex format example: bgcs.k12.oh.us, apps.sparcc.org
  const regex = /^\s*[a-zA-Z0-9-]{2,61}(?:\.[a-zA-Z0-9]{2,})*(\.[a-zA-Z]{2,3})$/;

  const domain = rowData.allowed_domains
    ? rowData.allowed_domains.replaceAll(" ", "").split(",")
    : [];
  if (domain.length === 0) {
    return false
  }
  return domain.every(d => d.match(regex));
};

/**
 *
 * @param section {string} The class of te section to print.
 * @param id {string} The element ID to print.
 * @param title {string} The name of the section to print.
 * @param printStyles {string} The string with the styles for print version.
 * @returns {boolean} The function return false after print window is closed.
 */
export const printSection = ({ section, id = null, title, printStyles }) => {
  let content = id ? document.getElementById(id).innerHTML : document.querySelector(section).innerHTML;
  let iframeEl = document.createElement('iframe');
  iframeEl.name = "iframeEl";
  iframeEl.style.position = 'absolute';
  iframeEl.style.width = '0px';
  iframeEl.style.height = '0px';
  document.body.appendChild(iframeEl);
  let frameDoc = iframeEl.contentWindow ? iframeEl.contentWindow : iframeEl.contentDocument;
  const frameContent = frameDoc.document.createElement('div');
  const style = frameDoc.document.createElement('style');
  setTimeout(() => {
    frameDoc.document.title = title;
    // Content to print.
    frameContent.innerHTML = content;
    frameDoc.document.body.appendChild(frameContent);
    // Styles.
    style.textContent = printStyles;
    frameDoc.document.head.appendChild(style);
    window.frames["iframeEl"].focus();
    window.frames["iframeEl"].print();
    document.body.removeChild(iframeEl);
    return false;
  }, [100])
}

export const colors = ['#D8A8D8', '#D36BC6', '#A03A67', '#773DBD', '#723973', '#1F1646'];

/**
 * The card Health benefits should show only if share is enabled.
 * @param {array} arr: the array of cards domain data
 * @param {boolean} share: the user share enabled value.
 * @returns {array}
 */
export function filterCards({ arr, flag }) {
  if (!flag) return arr.filter(card => !card.domain.includes("Teacher Salary and Benefits"));
  return arr;
}

/**
 * Used to validate unique names.
 * @param {array} arr: the list of names to be compared with the name param.
 * @param {string} key: if array of objects the key will be the value to compare with name param.
 * @param {string} name: the name value to compare.
 * @returns {boolean}
 */
export function nameValidation({ arr, key, name }) {
  if (key) return arr.every(el => el[key] !== name)
  return arr.every(el => el !== name)
}

/**
 * Checks if values in the range array are numbers.
 * @param {array} range: The min/max range values.
 * @returns {boolean}
 */
export function validRange({ range }) {
  return range.every(el => typeof el === 'number')
}

/**
 * Get the maximun and minimun value of a objet array group.
 * @param data {array} The object array to eval.
 * @param key {string} The key to get the value
 * @return {{min: number, max: number}|*}
 */
export function getMaxMin({data, key}) {
  if (data.length === 0) return { max: null, min: null };

  const ids = data.filter(object => object[key] !== null).map(object => {
    return object[key];
  });

  const max = Math.max(...ids);
  const min = Math.min(...ids);

  return {min, max}
}

/**
 *
 * @param data {object} The hvd_resume_salary data response.
 * @param level {string} The academic level BA/MA.
 * @return {object}
 */
export function resumeSalary({data, level}) {
  let keyByLevel = level === 'ba' ? '50k_ba' : '75k_ma';
  // Vars displayed in BA/MA levels.
  let salaryResumeVars = {
    career_technical_school: data?.career_technical_school,
    district_name: data?.district_name,
    file_year: data?.file_year,
    group_name: data?.group_name,
    irn: data?.irn,
    physical_address: data?.physical_address,
    salary_data: {
      level,
      data: data?.salary_data.map( item => ({
        year: item.year,
        negotiated_annual_raise: item.negotiated_annual_raise,
        [`base_salary_${level}`]: item[`base_salary_${level}`]
      }))
    }
  }

  if (salaryResumeVars?.salary_data?.data?.length === 0) {
    return {
      ...salaryResumeVars,
      static_data: [],
      table_data: [],
    }
  }

  return {
    ...salaryResumeVars,
    column_data: {
      [`${level}_base_salary`]: data?.metadata[`${level}_base_salary`],
      negotiated_annual_raise: data?.metadata.negotiated_annual_raise,
    },
    static_data: {
      [`base_salary_${level}`]: {
        label: data?.static_data[`base_salary_${level}`].label,
        value: data?.static_data[`base_salary_${level}`].value ?? null
      },
      current_contract_year: {
        ...data?.static_data.current_contract_year,
        value: data?.static_data.current_contract_year.value?.replace("year", "") || null
      },
      next_contract_renewal_year: {
        ...data?.static_data.next_contract_renewal_year,
        value: data?.static_data.next_contract_renewal_year.value?.replace("year", "") || null
      },
      negotiated_annual_raise: {
        label: data?.static_data.negotiated_annual_raise.label,
        value: data?.static_data.negotiated_annual_raise.value ?? null
      },
      [`years_to_${keyByLevel}`]: {
        label: data?.static_data[`years_to_${keyByLevel}`].label,
        value: data?.static_data[`years_to_${keyByLevel}`].value ?? null
      },
      [`lifetime_value_${level}`]: {
        value: data?.static_data[`lifetime_value_${level}`].value ? `$${data?.static_data[`lifetime_value_${level}`].value}`:  null,
        label: data?.static_data[`lifetime_value_${level}`].label
      },
    },
    table_data: {
      [`base_salary_${level}`] : data?.table_data[`base_salary_${level}`],
      negotiated_annual_raise: data?.table_data.negotiated_annual_raise,
      current_contract_year: data?.table_data.current_contract_year,
      next_contract_renewal_year: data?.table_data.next_contract_renewal_year,
      [`years_to_${keyByLevel}`]: {
        label: data?.table_data[`years_to_${keyByLevel}`].label,
        value: data?.table_data[`years_to_${keyByLevel}`].value ?? null,
        state: Math.floor(data?.table_data[`years_to_${keyByLevel}`].state) ?? null,
        rank_in_state: data?.table_data[`years_to_${keyByLevel}`].rank_in_state
      },
      [`lifetime_value_${level}`]: {
        value: data?.table_data[`lifetime_value_${level}`].value ? `$${data?.table_data[`lifetime_value_${level}`].value}`:  null,
        state: data?.table_data[`lifetime_value_${level}`].state ? `$${data?.table_data[`lifetime_value_${level}`].state}`:  null,
        rank_in_state: data?.table_data[`lifetime_value_${level}`].rank_in_state,
        label: data?.table_data[`lifetime_value_${level}`].label
      },
    }
  }
}

/**
 *
 * @param item
 * @param data
 * @returns object
 */
export function refDistrictHistogram (item, data) {
  return {
    y: item,
    color: "#F5B335",
    connectorWidth: data.length > 300 ? 3 : 1,
    marker: {
      radius: data.length > 60 ? 3 : 5,
    }
  }
}

export function updateChartTheme(chart) {
  let prevColor = chart.userOptions.chart.backgroundColor
  chart.update({
    chart: {
      backgroundColor: prevColor === '#333' ? '#fff' : '#333',
      height: chart.chartHeight,
    },
    title: {
      style: {
        color: prevColor,
      },
    },
    subtitle: {
      style: {
        color: prevColor,
      },
    },
    xAxis: [
      {
        labels: {
          style: {
            color: prevColor,
          },
        },
        title: {
          style: {
            color: prevColor,
          },
        },
      },
    ],
    yAxis: [{
      labels: {
        style: {
          color: prevColor,
        },
      },
      title: {
        style: {
          color: prevColor,
        },
      },
    }, { // Secondary yAxis
      labels: {
        style: {
          color: prevColor,
        },
      },
      title: {
        style: {
          color: prevColor,
        },
      },
    }],
    legend: {
      itemStyle: {
        "color": prevColor,
      },
      itemHoverStyle: {
        color: prevColor,
      }
    },
  });
}

// Build columns for table in salary toolkit board.
export function buildColumns(metadata, excludeSuffix) {
  return Object.keys(metadata)
    .filter(col => !col.includes(excludeSuffix))
    .map(col => ({
      field: col,
      headerName: metadata[col].label,
      minWidth: 150,
      flex: 1,
      cellClassName: (params) => params.value === null ? 'null-display' : '',
      valueFormatter: (params) => {
        if (params.value === null) return NULL_DISPLAY_TEXT;
        if (metadata[col].var_type === "year") return Math.floor(params.value);
        if (metadata[col].var_type) return valueFormatted(+params.value, metadata[col].var_type);
        return params.value;
      },
    }));
}

// Build rows for table in salary toolkit board.
export function buildRows(rows) {
  return rows.map(row => ({
    id: row.irn,
    irn: row.irn,
    district_name: row.district_name,
    catalog_year: row.year, // Added catalog year for MCD
    ...row.variables,
  }));
}
